import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  ReactFlow,
  ConnectionLineType,
  Node,
  Edge,
  ReactFlowProvider,
  ReactFlowInstance,
  NodeMouseHandler,
} from 'reactflow';
import styles from './organisationMapReactFlow.module.scss';
import CustomNode from 'views/CreateProductFlow/ProductOverview/GetReactFlow/customNode';
import { getLayoutedElements } from 'views/CompanySetup/OrganisationMap/OrganisationReactFlow/dagreTreeGenerator.util';
import { OrganizationNodeType } from './organisationReactFlow.util';

interface OrganisationMapReactFlowProps {
  initialNodes: Node[];
  initialEdges: Edge[];
  handleNodeClick?: NodeMouseHandler;
}
export interface OrganizationMapReset {
  resetView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

const emptyFunction = () => {};

const OrganisationMapReactFlow = forwardRef<
  OrganizationMapReset,
  OrganisationMapReactFlowProps
>(({ initialEdges, initialNodes, handleNodeClick }, ref) => {
  const { nodes, edges } = useMemo(() => {
    return getLayoutedElements(initialNodes, initialEdges);
  }, [initialNodes, initialEdges]);

  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const [hasMoved, setHasMoved] = useState<boolean>(false);

  const onInit = useCallback(
    instance => {
      setHasMoved(true);
      setReactFlowInstance(instance);
    },
    [nodes, edges]
  );
  const customNodeType = useMemo(() => {
    return {
      customNode: CustomNode,
    };
  }, [CustomNode]);

  const fitViewOptions = useMemo(() => {
    if (!nodes.length) return {};
    const focusNodes = nodes?.filter(item => {
      const node = item as OrganizationNodeType;
      return node.isFocusNode;
    });
    return {
      padding: 0.5,
      nodes: focusNodes ?? { id: nodes[0].id },
      duration: 200,
    };
  }, [nodes, reactFlowInstance]);

  const resetView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView(fitViewOptions);
      setHasMoved(false);
    }
  }, [reactFlowInstance, nodes]);

  const zoomIn = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomIn();
    }
  }, [reactFlowInstance]);

  const zoomOut = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomOut();
    }
  }, [reactFlowInstance]);

  useImperativeHandle(ref, () => ({
    resetView,
    zoomIn,
    zoomOut,
  }));

  const handleMove = useCallback(() => {
    setHasMoved(true);
  }, []);

  return (
    <div className={styles.reactFlowWrapper}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onInit={onInit}
          onMove={handleMove}
          connectionLineType={ConnectionLineType.SmoothStep}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={fitViewOptions}
          panOnDrag={true}
          nodeTypes={customNodeType}
          maxZoom={2}
          minZoom={0.1}
          zoomOnScroll={true}
          zoomOnPinch={true}
          //To enable nodeComponent's functions
          onNodeClick={handleNodeClick ?? emptyFunction}></ReactFlow>
      </ReactFlowProvider>
    </div>
  );
});

export default OrganisationMapReactFlow;
